<template>
  <section>
    <!--UPLOAD-->
    <form enctype="multipart/form-data" novalidate v-if="isInitial || isSaving">
      <div class="row">
        <div class="col-md-10">
          <h5 v-if="title !== ''">{{ title }}</h5>
          <p v-if="subtitle !== ''">{{ subtitle }}</p>
        </div>
        <div class="col-md-2">
          <div v-if="isShowSave && !isDontSave" class="custom-file-save">
            <button class="btn btn-primary btn-sm" @click="save">Save</button>
          </div>
        </div>
      </div>
      <div v-if="isReadonly" class="row">
        <div class="list-group">
          <div
            v-for="(file, idx) of currentFiles"
            :key="idx"
            class="list-group-item"
          >
            <a :href="file.url" target="_blank">
              <i class="far fa-file custom-file-icon"></i>
              <span class="custom-file-name">{{ file.name }}</span>
            </a>
          </div>
        </div>
      </div>
      <div v-if="!isReadonly" class="row">
        <div :class="{ 'col-md-12': !isShowList, 'col-md-8': isShowList }">
          <div class="dropbox">
            <input
              type="file"
              class="input-file"
              name="files[]"
              id="files"
              multiple
              :disabled="isSaving"
              :accept="accept"
              @change="filesChange"
            />
            <p v-if="isInitial">{{ instructions }}</p>
            <p v-if="isSaving">Uploading {{ fileCount }} image...</p>
          </div>
        </div>
        <div v-if="isShowList" class="col-md-4 custom-file-list">
          <div v-if="currentFiles.length > 0" class="list-group">
            <div
              v-for="(file, idx) of currentFiles"
              :key="idx"
              class="list-group-item"
            >
              <div v-if="allowRemovalExisting">
                <i class="far fa-file custom-file-icon"></i>
                <span class="custom-file-name">{{
                  formatFilename(file.name)
                }}</span>
                <a
                  v-if="allowRemovalExisting"
                  class="custom-file-remove"
                  @click="removeExistingFile(file.name)"
                >
                  <i class="fas fa-times"></i>
                </a>
              </div>
              <div v-else>
                <a @click="showFile(file)" class="custom-file-show">
                  <i class="far fa-file custom-file-icon"></i>
                  <span class="custom-file-name">{{
                    formatFilename(file.name)
                  }}</span>
                </a>
              </div>
            </div>
          </div>
          <div class="list-group">
            <div
              v-for="(file, idx) of attachedFiles"
              :key="idx"
              class="list-group-item"
            >
              <i class="custom-file-icon" :class="setFileIcon(file.type)"></i>
              <span class="custom-file-name">{{
                formatFilename(file.name)
              }}</span>
              <a class="custom-file-remove" @click="removeFile(file.name)">
                <i class="fas fa-times"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </form>
    <!--SUCCESS-->
    <div v-if="isSuccess">
      <p>
        <a href="javascript:void(0)" @click="reset()">Upload again</a>
      </p>
      <ul class="list-unstyled">
        <li v-for="(item, idx) in attachedFiles" :key="idx">
          <img
            :src="item"
            class="img-responsive img-thumbnail custom-image"
            :alt="item.originalName"
          />
        </li>
      </ul>
    </div>
    <!--FAILED-->
    <div v-if="isFailed">
      <h5>Upload failed.</h5>
      <p>
        <a href="javascript:void(0)" @click="reset()">Try again</a>
      </p>
      <pre>{{ uploadError }}</pre>
    </div>
  </section>
</template>
<script>
import * as axios from 'axios'
import globals from '@/Plugins/globals'
const STATUS_INITIAL = 0
const STATUS_SAVING = 1
const STATUS_SUCCESS = 2
const STATUS_FAILED = 3
export default {
  name: 'z-upload',
  props: {
    title: String,
    subtitle: String,
    instructions: String,
    endpoint: String,
    path: String,
    folder: String,
    id: {
      type: Number,
      default: null,
    },
    isShowList: {
      type: Boolean,
      default: false,
    },
    isShowSave: {
      type: Boolean,
      default: false,
    },
    isDontSave: {
      type: Boolean,
      default: false,
    },
    existingFiles: {
      type: Array,
      default: [],
    },
    accept: {
      type: String,
      default: 'image/*',
    },
    allowRemovalExisting: {
      type: Boolean,
      default: false,
    },
    isReadonly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      baseUrl: window.config ? window.config.apiUrl : '',
      storageUrl: window.config ? window.config.storageUrl : '',
      attachedFiles: [],
      uploadError: null,
      currentStatus: null,
      currentFiles: [],
    }
  },
  mounted() {
    this.reset()
    this.$emit('filesAdded', this.attachedFiles.length)
  },
  watch: {
    existingFiles() {
      this.currentFiles = JSON.parse(JSON.stringify(this.existingFiles))
      console.log('Current Files', this.currentFiles)
    },
  },
  emits: ['filesAdded', 'show', 'finished'],
  computed: {
    isInitial() {
      return this.currentStatus === STATUS_INITIAL
    },
    isSaving() {
      return this.currentStatus === STATUS_SAVING
    },
    isSuccess() {
      return this.currentStatus === STATUS_SUCCESS
    },
    isFailed() {
      return this.currentStatus === STATUS_FAILED
    },
  },
  methods: {
    setFileIcon(type) {
      let _class = ''
      _class = globals.fileIcon(type)
      // console.log('Setting File Icon', { type: type, class: _class })
      return _class
    },
    formatFilename(name) {
      let _name = name
      if (name.length > 30) {
        _name = name.substring(0, 25) + '...'
      }
      return _name
    },
    wait(ms) {
      return (x) => {
        return new Promise((resolve) => setTimeout(() => resolve(x), ms))
      }
    },
    reset() {
      // reset form to initial state
      this.currentStatus = STATUS_INITIAL
      this.uploadError = null
      this.attachedFiles = []
    },
    toBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.readAsDataURL(file)
        reader.onload = () => {
          let encoded = reader.result.toString().replace(/^data:(.*,)?/, '')
          if (encoded.length % 4 > 0) {
            encoded += '='.repeat(4 - (encoded.length % 4))
          }
          encoded = file.name + '|' + encoded
          resolve(encoded)
        } // resolve(reader.result)
        reader.onerror = (error) => reject(error)
      })
    },
    async getAttachedFilesBase64() {
      const filePathsPromises = []
      Array.from(this.attachedFiles).forEach((file) => {
        filePathsPromises.push(this.toBase64(file))
      })
      const filePaths = await Promise.all(filePathsPromises)
      const mappedFiles = filePaths.map((base64File) => ({
        name: base64File.substr(0, base64File.indexOf('|')),
        base64: base64File.substr(base64File.indexOf('|') + 1),
      }))
      return mappedFiles
    },
    removeExistingFile(filename) {
      // if (this.currentFiles.length === 1) {
      //   this.currentFiles = []
      // } else {
      this.currentFiles = this.currentFiles.filter((el) => {
        return el.name.toUpperCase() !== filename.toUpperCase()
      })
      // }
    },
    removeFile(filename) {
      const dt = new DataTransfer()
      const input = document.getElementById('files')
      const { files } = input
      for (let i = 0; i < files.length; i++) {
        const file = files[i]
        if (file.name !== filename) {
          dt.items.add(file)
        }
      }
      input.files = dt.files
      this.attachedFiles = dt.files
      this.$emit('filesRemoved', this.attachedFiles.length)
    },
    showFile(file) {
      this.$emit('show', file)
    },
    getAttachedFiles() {
      return this.attachedFiles
    },
    getExistingFiles() {
      return this.currentFiles
    },
    save() {
      if (this.attachedFiles.length > 0) {
        // upload data to the server
        const formData = new FormData()
        for (const i of Object.keys(this.attachedFiles)) {
          formData.append('files[]', this.attachedFiles[i])
        }
        this.currentStatus = STATUS_SAVING
        let api = `${location.origin}/${this.endpoint}`
        if (this.id !== null) {
          api += `/${this.id}`
        }
        if (this.folder !== null) {
          api += `/${this.folder}`
        }
        return axios
          .post(api, formData, {
            headers: {
              'Content-Type': `multipart/form-data;boundary=${Math.random().toString()}`,
            },
          })
          .then((response) => {
            console.log('Files Uploaded', response)
            if (response.data.success) {
              this.currentStatus = STATUS_SUCCESS
            } else {
              this.uploadError = response.data.errors
              this.currentStatus = STATUS_FAILED
            }
            let uploadedFiles = response.data.data
            this.attachedFiles = []
            this.$emit('finished', uploadedFiles)
            return uploadedFiles
          })
          .catch((err) => {
            this.uploadError = err.response
            this.currentStatus = STATUS_FAILED
            this.attachedFiles = []
            return null
          })
      } else {
        return null
      }
    },
    filesChange(event) {
      const validTypes = ['image/jpeg', 'image/png', 'image/gif']
      const files = Array.from(event.target.files)
      const invalidFiles = files.filter(
        (file) => !validTypes.includes(file.type)
      )

      if (invalidFiles.length > 0) {
        alert('Only image files (JPG, PNG, GIF) are allowed.')
        event.target.value = '' // Clear invalid selection
        this.attachedFiles = []
        return
      }

      // console.log('Files Changed', event)
      this.attachedFiles = event.target.files
      this.fileCount = this.attachedFiles.length
      // save it?
      if (!this.isShowSave && !this.isDontSave) {
        this.save()
      }
      if (this.isDontSave) {
        this.$emit('filesAdded', this.fileCount)
      }
      console.log('Attached Files', this.attachedFiles)
    },
  },
}
</script>
<style lang="scss">
.dropbox {
  outline: 2px dashed grey; /* the dash box */
  outline-offset: -10px;
  background: lightcyan;
  color: dimgray;
  padding: 10px 10px;
  min-height: 200px; /* minimum height */
  position: relative;
  cursor: pointer;
}
.input-file {
  opacity: 0; /* invisible but it's there! */
  width: 100%;
  height: 200px;
  position: absolute;
  cursor: pointer;
}
.dropbox:hover {
  background: lightblue; /* when mouse over to the drop zone, change color */
}
.dropbox p {
  font-size: 1.2em;
  text-align: center;
  padding: 50px 0;
}
.custom-image {
  width: 300px;
  height: 300px;
}
.custom-file-list {
  max-height: 400px;
  overflow-x: hidden;
  overflow-y: auto;
}
.custom-file-save {
  text-align: right;
}
.custom-file-icon {
  color: #7a7a7a;
}
.custom-file-name {
  margin-left: 10px;
  font-size: 0.8em;
}
.custom-file-remove {
  margin-left: 10px;
  color: red;
  text-align: right;
  right: 10px;
  top: 0;
  position: absolute;
  cursor: pointer;
}
.custom-file-show {
  cursor: pointer;
}
</style>
